/** Production binding of the authored frontier kit. CityGraph identities,
 * roads, parcel use and inventories stay stable; geometry is fitted once in
 * shared code and consumed by both the renderer and authoritative collision. */
import {frontierPlace,type FrontierPlacePlan,type FrontierPlaceKind,type PlaceBox} from '../buildings/frontierTown.js';
import {compileAsset} from '../proceduralAssetCompile.js';
import {planDoorSide} from '../buildings/plan.js';
import {tangentFrame} from './boxColliders.js';
import * as pmath from '../pmath.js';
import type {AssetVec3} from '../proceduralAssets.js';
import {USE_DWELLING,USE_BUNKHOUSE,USE_INN,USE_HEADMAN,USE_WORKSHOP,USE_SMITHY,USE_PRODUCTION,USE_MILL,USE_WAREHOUSE,USE_DEPOT,USE_FUEL_SHED,USE_MARKET_HALL,USE_CHAPEL,USE_SHRINE,USE_WATCHTOWER} from './citygraph.js';
export const FRONTIER_WORLD_VERSION=1;
const kinds=new Map<number,FrontierPlaceKind>([
 [USE_DWELLING,'home'],[USE_BUNKHOUSE,'home'],[USE_INN,'home'],[USE_HEADMAN,'home'],
 [USE_WORKSHOP,'workshop'],[USE_SMITHY,'workshop'],[USE_MILL,'workshop'],[USE_PRODUCTION,'workshop'],
 [USE_WAREHOUSE,'storehouse'],[USE_DEPOT,'storehouse'],[USE_FUEL_SHED,'storehouse'],
 [USE_MARKET_HALL,'market'],[USE_CHAPEL,'chapel'],[USE_SHRINE,'chapel'],[USE_WATCHTOWER,'watchtower'],
]);
interface Template {plan:FrontierPlacePlan;min:AssetVec3;max:AssetVec3}
// Bounded structural variants share compiled artwork rather than generating
// a new thousand-part asset for every house on every server/client query.
const templates=new Map<string,Template>();
function template(kind:FrontierPlaceKind,variant:number):Template {
 const key=`${kind}:${variant}`;let found=templates.get(key);if(found)return found;
 const plan=frontierPlace(kind,`world-frontier-v${FRONTIER_WORLD_VERSION}:${key}`);
 const compiled=[plan.asset,...plan.assetParts??[]].map(compileAsset);
 const min=[0,1,2].map(i=>Math.min(...compiled.map(c=>c.bounds.min[i]))) as AssetVec3;
 const max=[0,1,2].map(i=>Math.max(...compiled.map(c=>c.bounds.max[i]))) as AssetVec3;
 // Include the approach and cloth in parcel clearance, not just the shell.
 for(const p of [plan.entrance,plan.entryApproach])for(const i of [0,2]){min[i]=Math.min(min[i],p[i]-.65);max[i]=Math.max(max[i],p[i]+.65);}
 for(const c of plan.canopies){min[0]=Math.min(min[0],c.x-c.width/2);max[0]=Math.max(max[0],c.x+c.width/2);min[2]=Math.min(min[2],c.backZ,c.frontZ);max[2]=Math.max(max[2],c.backZ,c.frontZ);}
 found={plan,min,max};templates.set(key,found);return found;
}
export interface FrontierRecord {
 version:1;plan:FrontierPlacePlan;scale:number;quarterTurn:number;
 /** Template-space origin, mapped into the original building record frame. */
 offset:AssetVec3;
}
/** Null means the parcel requires the compact/multistorey program, not a
 * shrunken cottage with an unusable door. No unbounded fitting retries. */
export function frontierForRecord(b:Float64Array,o:number):FrontierRecord|null {
 const kind=kinds.get(b[o+15]);if(!kind)return null;
 const t=template(kind,(b[o+14]>>>0)%8),q=[0,2,1,3][planDoorSide(b[o+3],b[o+12])];
 const width=t.max[0]-t.min[0],depth=t.max[2]-t.min[2];
 const scale=Math.min(1.12,(b[o+4]-.2)/(q%2?depth:width),(b[o+5]-.2)/(q%2?width:depth));
 if(scale<.85)return null;
 const cx=(t.min[0]+t.max[0])/2,cz=(t.min[2]+t.max[2])/2;
 return {version:1,plan:t.plan,scale,quarterTurn:q,offset:[-cx,Math.max(0,b[o+17]||0),-cz]};
}
/** Local record coordinates; +Y stays life-size so doors and counters work. */
export function frontierLocal(f:FrontierRecord,p:readonly number[]):AssetVec3 {
 const x=(p[0]+f.offset[0])*f.scale,z=(p[2]+f.offset[2])*f.scale,q=f.quarterTurn;
 return [[x,z,-x,-z][q],p[1]+f.offset[1],[z,-x,-z,x][q]];
}
export function frontierBoxes(f:FrontierRecord,boxes:readonly PlaceBox[]):PlaceBox[]{
 return boxes.map(b=>({center:frontierLocal(f,b.center),size:[b.size[f.quarterTurn%2?2:0]*f.scale,b.size[1],b.size[f.quarterTurn%2?0:2]*f.scale]}));
}
/** Supported foundations and a walkable entrance over an existing graded lot.
 * Returned boxes are already in record coordinates. */
export function frontierSupport(f:FrontierRecord):PlaceBox[]{
 const boxes:PlaceBox[]=[];
 if(f.offset[1]<.03)return boxes;
 for(const floor of frontierBoxes(f,f.plan.floors)){
  const bottom=floor.center[1]-floor.size[1]/2;
  if(bottom<.01||bottom>f.offset[1]+.4)continue;
  boxes.push({center:[floor.center[0],(bottom-.25)/2,floor.center[2]],size:[floor.size[0],bottom+.25,floor.size[2]]});
 }
 const a=frontierLocal(f,f.plan.entryApproach),b=frontierLocal(f,f.plan.entrance);
 const top=f.offset[1]+.3,n=Math.ceil(top/.2),run=Math.max(1.2,pmath.hypot(b[0]-a[0],b[2]-a[2])),q=f.quarterTurn;
 for(let i=0;i<n;i++){
  const t=(i+.5)/n,h=top*(i+1)/n;
  boxes.push({center:[a[0]+(b[0]-a[0])*t,(h-.2)/2,a[2]+(b[2]-a[2])*t],size:q%2?[run/n,h+.2,1.55*f.scale]:[1.55*f.scale,h+.2,run/n]});
 }
 return boxes;
}
export function recordFrame(b:Float64Array,o:number){
 const f=tangentFrame(b[o],b[o+1],b[o+2]),c=pmath.cos(b[o+3]),s=pmath.sin(b[o+3]);
 const width=f.t1.map((v,i)=>v*c+f.t2[i]*s) as AssetVec3,up=f.up as AssetVec3;
 const depth=[width[1]*up[2]-width[2]*up[1],width[2]*up[0]-width[0]*up[2],width[0]*up[1]-width[1]*up[0]] as AssetVec3;
 const point=(p:readonly number[])=>[0,1,2].map(i=>b[o+i]+width[i]*p[0]+up[i]*p[1]+depth[i]*p[2])as AssetVec3;
 return {width,up,depth,point};
}
